import React, { useState, ChangeEvent } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Switch,
  FormControlLabel,
} from "@mui/material";
import * as Yup from "yup";

export type DiscountOn = "PRICE" | "CREDITS" | "BOTH";
export type DiscountType = "PERCENTAGE" | "FIXED_AMOUNT";
export type DiscountStatus = "ACTIVE" | "INACTIVE" | "EXPIRED";

export interface CreatePlanDiscountPayload {
  plan_id: number;
  discount_on: DiscountOn;
  discount_type: DiscountType;
  discount_value: number;
  credit_bonus?: number | null;
  start_at: string;
  end_at: string;
  status: DiscountStatus;
  max_usage: number;
  max_usage_per_user: number;
  tnc: string;
  can_apply: boolean;
}

interface CreatePlanDiscountProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreatePlanDiscountPayload) => void;
  planId: number;
}

const schema = Yup.object().shape({
  discount_on: Yup.mixed<DiscountOn>()
    .oneOf(["PRICE", "CREDITS", "BOTH"], "Invalid discount on")
    .required("Required"),
  discount_type: Yup.mixed<DiscountType>()
    .oneOf(["PERCENTAGE", "FIXED_AMOUNT"], "Invalid type")
    .required("Required"),
  discount_value: Yup.number().min(0, "Must be ≥ 0").required("Required"),
  credit_bonus: Yup.number().nullable().min(0, "Must be ≥ 0"),
  start_at: Yup.date()
    .required("Start date required")
    .typeError("Invalid date"),
  end_at: Yup.date()
    .min(Yup.ref("start_at"), "End must be after start")
    .required("End date required")
    .typeError("Invalid date"),
  status: Yup.mixed<DiscountStatus>()
    .oneOf(["ACTIVE", "INACTIVE", "EXPIRED"], "Invalid status")
    .required("Required"),
  max_usage: Yup.number().min(0, "Must be ≥ 0").required("Required"),
  max_usage_per_user: Yup.number().min(0, "Must be ≥ 0").required("Required"),
  tnc: Yup.string().required("T&C required"),
  can_apply: Yup.boolean().required(),
});

const CreatePlanDiscount: React.FC<CreatePlanDiscountProps> = ({
  open,
  onClose,
  onSubmit,
  planId,
}) => {
  const initial = {
    discount_on: "PRICE" as DiscountOn,
    discount_type: "PERCENTAGE" as DiscountType,
    discount_value: 0,
    credit_bonus: null,
    start_at: null,
    end_at: null,
    status: "ACTIVE" as DiscountStatus,
    max_usage: 1,
    max_usage_per_user: 1,
    tnc: "",
    can_apply: false,
  };
  const [vals, setVals] = useState(initial);
  const [errs, setErrs] = useState<
    Partial<Record<keyof typeof initial, string>>
  >({});

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    let v: any = type === "checkbox" ? checked : value;
    if (type === "number") v = Number(value);
    setVals((p) => ({ ...p, [name]: v }));
  };

  const handleSelect = (e: any) => {
    const { name, value } = e.target;
    setVals((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      const raw = {
        ...vals,
        plan_id: planId,
        start_at: vals.start_at ? new Date(vals.start_at).toISOString() : "",
        end_at: vals.end_at ? new Date(vals.end_at).toISOString() : "",
      };
      await schema.validate(raw, { abortEarly: false });
      setErrs({});
      onSubmit(raw);
    } catch (err: any) {
      const fe: any = {};
      err.inner?.forEach((e: any) => {
        fe[e.path] = e.message;
      });
      setErrs(fe);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>Create Plan Discount</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <FormControl fullWidth error={Boolean(errs.discount_on)}>
            <InputLabel>Discount On</InputLabel>
            <Select
              name="discount_on"
              value={vals.discount_on}
              onChange={handleSelect}
              label="Discount On"
            >
              <MenuItem value="PRICE">Price</MenuItem>
              <MenuItem value="CREDITS">Credits</MenuItem>
              <MenuItem value="BOTH">Both</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth error={Boolean(errs.discount_type)}>
            <InputLabel>Type</InputLabel>
            <Select
              name="discount_type"
              value={vals.discount_type}
              onChange={handleSelect}
              label="Type"
            >
              <MenuItem value="PERCENTAGE">Percentage</MenuItem>
              <MenuItem value="FIXED_AMOUNT">Fixed Amount</MenuItem>
            </Select>
          </FormControl>

          <TextField
            name="discount_value"
            label="Discount Value"
            type="number"
            inputProps={{ min: 0 }}
            value={vals.discount_value}
            onChange={handleChange}
            error={Boolean(errs.discount_value)}
            helperText={errs.discount_value}
            fullWidth
          />

          <TextField
            name="credit_bonus"
            label="Credit Bonus"
            type="number"
            inputProps={{ min: 0 }}
            value={vals.credit_bonus ?? ""}
            onChange={handleChange}
            error={Boolean(errs.credit_bonus)}
            helperText={errs.credit_bonus}
            fullWidth
          />

          <TextField
            name="start_at"
            label="Start At"
            type="datetime-local"
            InputLabelProps={{ shrink: true }}
            value={vals.start_at}
            onChange={handleChange}
            error={Boolean(errs.start_at)}
            helperText={errs.start_at}
            fullWidth
          />

          <TextField
            name="end_at"
            label="End At"
            type="datetime-local"
            InputLabelProps={{ shrink: true }}
            value={vals.end_at}
            onChange={handleChange}
            error={Boolean(errs.end_at)}
            helperText={errs.end_at}
            fullWidth
          />

          <FormControlLabel
            control={
              <Switch
                name="can_apply"
                checked={vals.can_apply}
                onChange={handleChange}
              />
            }
            label="Can Apply"
          />

          <FormControl fullWidth error={Boolean(errs.status)}>
            <InputLabel>Status</InputLabel>
            <Select
              name="status"
              value={vals.status}
              onChange={handleSelect}
              label="Status"
            >
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="INACTIVE">Inactive</MenuItem>
              <MenuItem value="EXPIRED">Expired</MenuItem>
            </Select>
          </FormControl>

          <TextField
            name="max_usage"
            label="Max Usage"
            type="number"
            inputProps={{ min: 0 }}
            value={vals.max_usage}
            onChange={handleChange}
            error={Boolean(errs.max_usage)}
            helperText={errs.max_usage}
            fullWidth
          />

          <TextField
            name="max_usage_per_user"
            label="Max Usage/User"
            type="number"
            inputProps={{ min: 0 }}
            value={vals.max_usage_per_user}
            onChange={handleChange}
            error={Boolean(errs.max_usage_per_user)}
            helperText={errs.max_usage_per_user}
            fullWidth
          />

          <TextField
            name="tnc"
            label="Terms & Conditions"
            value={vals.tnc}
            onChange={handleChange}
            error={Boolean(errs.tnc)}
            helperText={errs.tnc}
            multiline
            rows={3}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export default CreatePlanDiscount;
